import { Box, FormLabel } from '@mui/material';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DatePicker, DatePickerProps, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import useMarket from 'stores/useMarket';
import FormError from './FormError';

type DateInputProps = DatePickerProps<DateTime> & {
    name: string;
    datePickerSlotProps?: DatePickerProps<DateTime>['slotProps'];
    showSeparateLabel?: boolean;
};

export default function DateInput({ name, datePickerSlotProps, ...datePickerProps }: DateInputProps) {
    const {
        label,
        disabled,
        showSeparateLabel = false,
    } = datePickerProps;
    const { control, formState: { errors } } = useFormContext();

    const { locale } = useMarket();

    const errorMessage = typeof errors[name]?.message === 'string' ? errors[name]?.message as string : undefined;

    return (
        <Box
            marginBottom={errors[name]?.message && !disabled ? 0 : 2.5}
            data-testid={`date-input-${name}`}
        >
            {showSeparateLabel ? (
                <FormLabel htmlFor={name}>
                    {label}
                </FormLabel>
            ) : null}
            <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={locale}>
                <Controller
                    name={name}
                    control={control}
                    render={({ field }) => {
                        const { onChange, value, ...fieldRest } = field;

                        return (
                            <DatePicker
                                slotProps={datePickerSlotProps}
                                sx={{ width: '100%' }}
                                label={!showSeparateLabel ? label : undefined}
                                value={value ? DateTime.fromISO(value) : null}
                                onChange={(date) => onChange(date?.toISO())}
                                {...fieldRest}
                                disabled={disabled}
                                format="yyyy-MM-dd"
                            />
                        );
                    }}
                    {...datePickerProps}
                />
            </LocalizationProvider>
            <FormError show={Boolean(errorMessage) && !disabled}>
                {errorMessage}
            </FormError>
        </Box>
    );
}
